import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, X, LogIn, MapPin, Minus, PlayCircle, Pause, Play, Volume2, Square } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { TOUR_STEPS, TOUR_ACCOUNTS, TOUR_UI } from "@/lib/tourSteps";

const KEY = "bk_tour_step";
const AUTO_KEY = "bk_tour_auto";
export const AUTO_SECONDS = 30;
const readStep = () => {
  const v = localStorage.getItem(KEY);
  return v === null ? null : Number(v);
};

export const startTour = () => { localStorage.setItem(KEY, "0"); localStorage.setItem(AUTO_KEY, "1"); };

function SignInPrompt({ step, ui, onSwitch }) {
  return (
    <div className="mt-4 rounded-xl bg-white/10 border border-white/15 p-3 text-xs" data-testid="tour-signin-prompt">
      <div className="text-white/70">{ui.signIn}</div>
      <div className="mt-1 font-mono text-[13px] text-white" data-testid="tour-signin-email">{TOUR_ACCOUNTS[step.role]}</div>
      <div className="text-white/60 mt-0.5">{ui.pwd}: <span className="font-mono text-white">Demo@123</span></div>
      <button onClick={onSwitch} data-testid="tour-switch-account" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#E86A2E] px-3 py-1.5 font-semibold text-white hover:bg-[#d65d24]">
        <LogIn className="w-3.5 h-3.5" /> {ui.switch}
      </button>
    </div>
  );
}

function NarrateButton({ playing, ui, onToggle }) {
  return (
    <button onClick={onToggle} data-testid="tour-narrate" aria-pressed={playing}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${playing ? "bg-[#F5B981] text-[color:var(--bk-navy)]" : "border border-white/25 hover:bg-white/10"}`}>
      {playing ? <Square className="w-3 h-3" /> : <Volume2 className="w-3.5 h-3.5" />} {playing ? ui.stopNarration : ui.narrate}
    </button>
  );
}

function AutoControl({ auto, left, ui, onToggle }) {
  return (
    <div className="mt-4 flex items-center gap-3" data-testid="tour-auto">
      <button onClick={onToggle} data-testid="tour-auto-toggle" className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1 text-xs font-semibold hover:bg-white/10">
        {auto ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {auto ? ui.pause : ui.resume}
      </button>
      {auto && (
        <div className="flex-1 flex items-center gap-2 text-[11px] text-white/60">
          <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#F5B981] transition-[width] duration-1000 ease-linear" style={{ width: `${(left / AUTO_SECONDS) * 100}%` }} /></div>
          <span data-testid="tour-countdown">{left > 0 ? `${left}s` : ui.waiting}</span>
        </div>
      )}
    </div>
  );
}

export function TourOverlay() {
  const [idx, setIdx] = useState(readStep);
  const [mini, setMini] = useState(false);
  const [auto, setAuto] = useState(() => localStorage.getItem(AUTO_KEY) === "1");
  const [left, setLeft] = useState(AUTO_SECONDS);
  const [narrating, setNarrating] = useState(false);
  const audioRef = useRef(null);
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const loc = useLocation();
  const nav = useNavigate();
  const ui = TOUR_UI[lang] || TOUR_UI.en;
  const step = idx === null || Number.isNaN(idx) ? null : TOUR_STEPS[idx];
  const rightRole = !!step && user?.role === step.role;
  const onScreen = rightRole && loc.pathname === step.path;

  useEffect(() => { setIdx(readStep()); }, [loc.pathname]);

  const go = useCallback((i) => {
    if (i >= TOUR_STEPS.length) {
      localStorage.removeItem(KEY); setIdx(null); toast.success(ui.done); return;
    }
    localStorage.setItem(KEY, String(i)); setIdx(i);
    const s = TOUR_STEPS[i];
    if (user?.role === s.role) nav(s.path);
  }, [nav, user?.role, ui.done]);

  const stopNarration = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setNarrating(false);
  }, []);
  useEffect(() => stopNarration, [idx, lang, stopNarration]);
  const toggleNarration = () => {
    if (narrating) { stopNarration(); return; }
    const a = new Audio(`/tour-audio/step-${String(idx + 1).padStart(2, "0")}-${lang === "hi" ? "hi" : "en"}.mp3`);
    a.onended = () => { audioRef.current = null; setNarrating(false); };
    a.onerror = () => { audioRef.current = null; setNarrating(false); };
    audioRef.current = a;
    setNarrating(true);
    a.play().catch(() => setNarrating(false));
  };

  useEffect(() => { setLeft(AUTO_SECONDS); }, [idx, onScreen, auto]);
  useEffect(() => {
    if (!auto || !onScreen) return undefined;
    // Each screen stays at least as long as its narration.
    if (left <= 0) { if (!narrating) go(idx + 1); return undefined; }
    const h = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(h);
  }, [auto, onScreen, left, idx, go, narrating]);

  if (!step) return null;
  const [title, body] = step[lang] || step.en;
  const end = () => { stopNarration(); localStorage.removeItem(KEY); setIdx(null); };
  const toggleAuto = () => { const v = !auto; setAuto(v); localStorage.setItem(AUTO_KEY, v ? "1" : "0"); };
  const onSwitch = async () => {
    if (user) await logout();
    window.location.assign(`/login?email=${encodeURIComponent(TOUR_ACCOUNTS[step.role])}`);
  };
  const last = idx === TOUR_STEPS.length - 1;

  if (mini) {
    return (
      <button onClick={() => setMini(false)} data-testid="tour-expand" className="fixed bottom-4 right-4 z-[60] inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-2xl brand-body" style={{ background: "rgba(11,18,32,0.94)" }}>
        <PlayCircle className="w-4 h-4 text-[#F5B981]" /> {ui.step} {idx + 1}/{TOUR_STEPS.length}{auto && onScreen ? ` · ${left}s` : ""}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-[60] sm:w-[380px] rounded-2xl text-white shadow-2xl border border-white/10 p-5 brand-body"
      style={{ background: "rgba(11,18,32,0.94)", backdropFilter: "blur(16px)" }} data-testid="tour-overlay">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F5B981]" data-testid="tour-progress">
          {ui.step} {idx + 1} {ui.of} {TOUR_STEPS.length} · {ui.acts[step.role]}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMini(true)} data-testid="tour-minimize" className="text-white/60 hover:text-white" aria-label={ui.minimize}><Minus className="w-4 h-4" /></button>
          <button onClick={end} data-testid="tour-end" className="text-white/60 hover:text-white" aria-label={ui.end}><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#E86A2E] transition-all" style={{ width: `${((idx + 1) / TOUR_STEPS.length) * 100}%` }} /></div>
      <h3 className="mt-4 brand-heading text-lg leading-snug" data-testid="tour-title">{title}</h3>
      <p className="mt-2 text-sm text-white/75 leading-relaxed" data-testid="tour-body">{body}</p>
      <div className="mt-3"><NarrateButton playing={narrating} ui={ui} onToggle={toggleNarration} /></div>
      {!rightRole && <SignInPrompt step={step} ui={ui} onSwitch={onSwitch} />}
      {rightRole && !onScreen && (
        <button onClick={() => nav(step.path)} data-testid="tour-open-screen" className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1.5 text-xs font-semibold hover:bg-white/10">
          <MapPin className="w-3.5 h-3.5" /> {ui.open}
        </button>
      )}
      {onScreen && <AutoControl auto={auto} left={left} ui={ui} onToggle={toggleAuto} />}
      <div className="mt-5 flex items-center justify-between">
        <button onClick={() => go(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="tour-back" className="inline-flex items-center gap-1 text-sm text-white/75 hover:text-white disabled:opacity-30">
          <ArrowLeft className="w-4 h-4" /> {ui.back}
        </button>
        <button onClick={() => go(idx + 1)} data-testid="tour-next" className="inline-flex items-center gap-1.5 rounded-full bg-white text-[color:var(--bk-navy)] px-4 py-2 text-sm font-semibold hover:bg-white/90">
          {last ? ui.finish : ui.next} {!last && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
